/**
 * Tenant-specific rent/deposit discount editor (COL-360).
 *
 * Sits inside the manual-override panel, below the base rent/deposit inputs.
 * The visual and behavioural contract comes from the design canvas attached to
 * COL-360 (`ManualOverride.dc.html`): amount plus a €/% toggle per discount, a
 * duration in months or the whole term, the resulting figure shown against the
 * struck-through base, and a plain-language summary underneath.
 *
 * The separation this UI has to make obvious is that the inputs above it change
 * what the UNIT costs (and sync to the room), while everything in here applies
 * to this tenant only and leaves the room's own price alone.
 *
 * Admin-facing only. Neither the summary nor the reason is ever shown to
 * tenants or owners, or sent in email.
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Percent, X } from "lucide-react";
import {
  effectiveDepositEUR,
  effectiveRentForMonthEUR,
  formatDiscountSummary,
  type DiscountMode,
} from "@/lib/rent-discount";

export interface TenantDiscountValue {
  enabled: boolean;
  rentMode: DiscountMode;
  rentAmount: string;
  months: string;
  fullTerm: boolean;
  depositMode: DiscountMode;
  depositAmount: string;
}

export const EMPTY_DISCOUNT: TenantDiscountValue = {
  enabled: false,
  rentMode: "eur",
  rentAmount: "",
  months: "3",
  fullTerm: false,
  depositMode: "eur",
  depositAmount: "",
};

/** The booking shape the resolvers need, built from the in-progress form state. */
export function discountFieldsFromValue(
  value: TenantDiscountValue,
  baseRent: number,
  baseDeposit: number,
  startMonth: string | null,
  options?: { depositLocked?: boolean }
) {
  if (!value.enabled) {
    return { monthly_rent: baseRent, deposit_amount: baseDeposit };
  }
  const months = value.fullTerm ? null : Number(value.months);
  return {
    monthly_rent: baseRent,
    deposit_amount: baseDeposit,
    rent_discount_mode: value.rentMode,
    rent_discount_value: Number(value.rentAmount) || null,
    rent_discount_months:
      Number.isFinite(months) && Number(months) > 0 ? months : null,
    rent_discount_start_month: startMonth,
    deposit_discount_mode: value.depositMode,
    // A locked deposit cannot carry a discount, so the figure never reaches the
    // resolvers while the lock is on.
    deposit_discount_value: options?.depositLocked
      ? null
      : Number(value.depositAmount) || null,
  };
}

function ModeToggle({
  mode,
  onToggle,
  disabled,
  label,
}: {
  mode: DiscountMode;
  onToggle: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={disabled}
      onClick={onToggle}
      aria-label={`${label} — switch to ${mode === "eur" ? "percent" : "euro"}`}
      className="h-9 w-10 shrink-0 rounded-lg px-0 text-sm font-medium"
    >
      {mode === "eur" ? "€" : <Percent className="h-3.5 w-3.5" />}
    </Button>
  );
}

function Resulting({
  caption,
  value,
  base,
  note,
}: {
  caption: string;
  value: number;
  base: number;
  note?: string;
}) {
  const changed = Math.abs(value - base) >= 0.01;
  return (
    <div className="sm:ml-auto sm:text-right">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {caption}
      </div>
      <div className="flex items-baseline gap-2 sm:justify-end">
        <span className="text-lg font-semibold tabular-nums">
          €{Math.round(value).toLocaleString("de-DE")}
        </span>
        {changed ? (
          <span className="text-xs text-muted-foreground line-through tabular-nums">
            €{Math.round(base).toLocaleString("de-DE")}
          </span>
        ) : (
          note && <span className="text-xs text-muted-foreground">{note}</span>
        )}
      </div>
    </div>
  );
}

export function TenantDiscountFields({
  value,
  onChange,
  baseRent,
  baseDeposit,
  startMonth,
  depositLocked = false,
}: {
  value: TenantDiscountValue;
  onChange: (next: TenantDiscountValue) => void;
  /** Base monthly rent currently in the form above. */
  baseRent: number;
  /** Base deposit currently in the form above. */
  baseDeposit: number;
  /** Month the discount is anchored to, for the summary's date range. */
  startMonth: string | null;
  /** Deposit settled — deposit amount and its discount are read-only. */
  depositLocked?: boolean;
}) {
  const patch = (over: Partial<TenantDiscountValue>) =>
    onChange({ ...value, ...over });

  if (!value.enabled) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange({ ...EMPTY_DISCOUNT, enabled: true })}
        className="h-9 rounded-lg text-xs font-medium"
      >
        Add tenant-specific discount
      </Button>
    );
  }

  const resolved = discountFieldsFromValue(
    value,
    baseRent,
    baseDeposit,
    startMonth,
    { depositLocked }
  );
  // Priced at the anchor month so the readout matches the first discounted
  // month rather than whatever today happens to be.
  const newRent = effectiveRentForMonthEUR(resolved, startMonth);
  const newDeposit = effectiveDepositEUR(resolved);
  const summary = formatDiscountSummary(resolved, { depositLocked });

  return (
    <div className="rounded-xl border border-amber-300/70 bg-white/70 p-4 dark:border-amber-800/50 dark:bg-amber-950/10">
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-[11px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">
          Tenant-specific discount
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange({ ...EMPTY_DISCOUNT, enabled: false })}
          className="h-7 rounded-lg px-2 text-xs"
        >
          <X className="mr-1 h-3.5 w-3.5" />
          Remove
        </Button>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-background p-3">
          <span className="min-w-[6.5rem] pb-2 text-xs font-semibold">
            Rent discount
          </span>
          <div className="space-y-1.5">
            <Label
              htmlFor="disc-rent"
              className="text-[11px] text-muted-foreground"
            >
              Amount
            </Label>
            <div className="flex items-center gap-1.5">
              <Input
                id="disc-rent"
                type="number"
                min="0"
                step="0.01"
                value={value.rentAmount}
                onChange={(e) => patch({ rentAmount: e.target.value })}
                className="h-9 w-24 rounded-lg text-sm"
                placeholder="0"
              />
              <ModeToggle
                mode={value.rentMode}
                label="Rent discount unit"
                onToggle={() =>
                  patch({ rentMode: value.rentMode === "eur" ? "pct" : "eur" })
                }
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label
              htmlFor="disc-months"
              className="text-[11px] text-muted-foreground"
            >
              Duration
            </Label>
            <div className="flex items-center gap-1.5">
              <Input
                id="disc-months"
                type="number"
                min="1"
                step="1"
                value={value.fullTerm ? "" : value.months}
                disabled={value.fullTerm}
                onChange={(e) => patch({ months: e.target.value })}
                className="h-9 w-20 rounded-lg text-sm"
                placeholder="—"
              />
              <span className="text-[11px] text-muted-foreground">months</span>
              <Button
                type="button"
                variant={value.fullTerm ? "default" : "outline"}
                size="sm"
                onClick={() => patch({ fullTerm: !value.fullTerm })}
                className="h-9 rounded-lg text-xs"
              >
                Full term
              </Button>
            </div>
          </div>
          <Resulting
            caption="New rent"
            value={newRent}
            base={baseRent}
            note="unchanged"
          />
        </div>

        <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-background p-3">
          <span className="min-w-[6.5rem] pb-2 text-xs font-semibold">
            Deposit discount
          </span>
          <div className="space-y-1.5">
            <Label
              htmlFor="disc-deposit"
              className="text-[11px] text-muted-foreground"
            >
              Amount
            </Label>
            <div className="flex items-center gap-1.5">
              <Input
                id="disc-deposit"
                type="number"
                min="0"
                step="0.01"
                value={depositLocked ? "" : value.depositAmount}
                disabled={depositLocked}
                onChange={(e) => patch({ depositAmount: e.target.value })}
                className="h-9 w-24 rounded-lg text-sm"
                placeholder="0"
              />
              <ModeToggle
                mode={value.depositMode}
                label="Deposit discount unit"
                disabled={depositLocked}
                onToggle={() =>
                  patch({
                    depositMode: value.depositMode === "eur" ? "pct" : "eur",
                  })
                }
              />
            </div>
          </div>
          {depositLocked ? (
            <div className="flex items-center gap-1.5 pb-2 text-xs text-muted-foreground sm:ml-auto">
              <Lock className="h-3.5 w-3.5" />
              Deposit is settled — only rent can be discounted
            </div>
          ) : (
            <Resulting
              caption="New deposit"
              value={newDeposit}
              base={baseDeposit}
              note="unchanged"
            />
          )}
        </div>
      </div>

      {summary && (
        <p className="mt-3 text-xs leading-relaxed text-amber-800 dark:text-amber-300">
          {summary}
        </p>
      )}
    </div>
  );
}
